import moment from "moment-timezone";
import { BeaconFundRequest, BeaconAllocation } from "@/lib/api/fund";
import { CommonDestinationTimezone } from "@/lib/utils";

export function calculateCustomerSummaries(
  fundRequests: BeaconFundRequest[],
  allocations: BeaconAllocation[],
  selectedYears: number[]
) {
  // Filter fund requests by customer
  const filteredRequests = fundRequests.filter((r: BeaconFundRequest) => {
    const requestYear = moment.tz(r.request_date || r.created_at, CommonDestinationTimezone).year();
    return (selectedYears.length === 0 || selectedYears.includes(requestYear)) &&
      r.Customer && !r.Employee; // Customer requests only
  });

  // Create flat data structure for grouping
  const monthlyMap = new Map<string, {
    customer_id: string;
    customer_name: string;
    month_year: string;
    allocated: number;
    granted: number;
    disbursed: number;
    count: number;
  }>();

  // Group by customer and month based on request_date
  filteredRequests.forEach((request: BeaconFundRequest) => {
    if (request.Customer) {
      const momentDate = moment.tz(request.request_date || request.created_at, CommonDestinationTimezone);
      const monthYear = `${momentDate.format("MMM")} ${momentDate.year()}`;
      const key = `${request.customer_id}-${monthYear}`;
      
      const existing = monthlyMap.get(key) || {
        customer_id: request.customer_id!,
        customer_name: request.Customer.long_name,
        month_year: monthYear,
        allocated: 0,
        granted: 0,
        disbursed: 0,
        count: 0,
      };
      
      // Find corresponding allocation for this year to get allocated amount
      const requestYear = momentDate.year();

      if (existing.allocated === 0) {
        const allocation = allocations.filter((a: BeaconAllocation) =>
          a.customer_id === request.customer_id &&
          a.year === requestYear
        ).reduce((acc, item) => {
          return {
            ...item,
            amount_allocated: (acc.amount_allocated ?? 0) + item.amount_allocated,
          }
        }, {} as any);
        existing.allocated = parseFloat(allocation.amount_allocated || "0");
      }
      
      // Add granted/disbursed amounts from request
      if (request.has_been_granted) {
        existing.granted += parseFloat(request.amount_granted || "0");
      }
      if (request.has_been_dispersed) {
        existing.disbursed += parseFloat(request.amount_granted || "0");
      }
      existing.count += 1;
      
      monthlyMap.set(key, existing);
    }
  });

  return Array.from(monthlyMap.values()).sort((a, b) => {
    // First sort by customer name
    const nameCompare = a.customer_name.localeCompare(b.customer_name);
    if (nameCompare !== 0) return nameCompare;
    
    // Then by date - parse month year properly
    const [monthA, yearA] = a.month_year.split(' ');
    const [monthB, yearB] = b.month_year.split(' ');
    const dateA = new Date(`${monthA} 1, ${yearA}`);
    const dateB = new Date(`${monthB} 1, ${yearB}`);
    return dateB.getTime() - dateA.getTime(); // Most recent first
  });
}

export function calculateEmployeeSummaries(
  fundRequests: BeaconFundRequest[],
  allocations: BeaconAllocation[],
  selectedYears: number[]
) {
  // Filter fund requests by employee
  const filteredRequests = fundRequests.filter((r: BeaconFundRequest) => {
    const momentDate = moment.tz(r.request_date || r.created_at, CommonDestinationTimezone);
    const requestYear = r.year || momentDate.year();
    return (selectedYears.length === 0 || selectedYears.includes(requestYear)) &&
      r.Employee && !r.Customer; // Employee requests only
  });

  // Create flat data structure for grouping
  const monthlyMap = new Map<string, {
    employee_id: string;
    employee_name: string;
    month_year: string;
    allocated: number;
    granted: number;
    disbursed: number;
    count: number;
  }>();

  // Group by employee and month based on request_date
  filteredRequests.forEach((request: BeaconFundRequest) => {
    if (request.Employee) {
      const momentDate = moment.tz(request.request_date || request.created_at, CommonDestinationTimezone);
      const monthYear = `${momentDate.format("MMM")} ${momentDate.year()}`;
      const key = `${request.employee_id}-${monthYear}`;
      
      const existing = monthlyMap.get(key) || {
        employee_id: request.employee_id!,
        employee_name: request.Employee.name,
        month_year: monthYear,
        allocated: 0,
        granted: 0,
        disbursed: 0,
        count: 0,
      };
      
      // Find corresponding allocation for this year to get allocated amount
      const requestYear = momentDate.year();
      
      if (existing.allocated === 0) {
        const allocation = allocations.filter((a: BeaconAllocation) =>
          a.employee_id === request.employee_id &&
          a.year === requestYear
        ).reduce((acc, item) => {
          return {
            ...item,
            amount_allocated: (acc.amount_allocated ?? 0) + item.amount_allocated,
          }
        }, {} as any);
        existing.allocated = parseFloat(allocation.amount_allocated || "0");
      }
      
      // Add granted/disbursed amounts from request
      if (request.has_been_granted) {
        existing.granted += parseFloat(request.amount_granted || "0");
      }
      if (request.has_been_dispersed) {
        existing.disbursed += parseFloat(request.amount_granted || "0");
      }
      existing.count += 1;
      
      monthlyMap.set(key, existing);
    }
  });

  return Array.from(monthlyMap.values()).sort((a, b) => {
    // First sort by employee name
    const nameCompare = a.employee_name.localeCompare(b.employee_name);
    if (nameCompare !== 0) return nameCompare;
    
    // Then by date - parse month year properly
    const [monthA, yearA] = a.month_year.split(' ');
    const [monthB, yearB] = b.month_year.split(' ');
    const dateA = new Date(`${monthA} 1, ${yearA}`);
    const dateB = new Date(`${monthB} 1, ${yearB}`);
    return dateB.getTime() - dateA.getTime(); // Most recent first
  });
}

export function calculateMonthlySummaries(
  allocations: BeaconAllocation[],
  fundRequests: BeaconFundRequest[],
  selectedYears: number[]
) {
  // First, calculate total allocations per year
  const yearlyAllocations = new Map<number, number>();
  allocations
    .filter((a: BeaconAllocation) => selectedYears.length === 0 || selectedYears.includes(a.year))
    .forEach((allocation: BeaconAllocation) => {
      const existing = yearlyAllocations.get(allocation.year) || 0;
      yearlyAllocations.set(allocation.year, existing + parseFloat(allocation.amount_allocated || "0"));
    });

  const monthlyMap = new Map<string, {
    month_year: string;
    year: number;
    allocated: number;
    requested: number;
    granted: number;
    disbursed: number;
    count: number;
  }>();

  // Process fund requests for granted and disbursed amounts by month
  fundRequests
    .filter((r: BeaconFundRequest) => {
      const year = r.year!;
      return selectedYears.length === 0 || selectedYears.includes(year);
    })
    .forEach((request: BeaconFundRequest) => {
      const momentDate = moment.tz(request.request_date || request.created_at, CommonDestinationTimezone);
      const monthYear = `${momentDate.format("MMM")} ${momentDate.year()}`;
      const year = momentDate.year();
      
      const existing = monthlyMap.get(monthYear) || {
        month_year: monthYear,
        year: year,
        allocated: yearlyAllocations.get(year) || 0,
        requested: 0,
        granted: 0,
        disbursed: 0,
        count: 0,
      };
      
      // Add the requested amount (using amount_granted as the requested amount)
      existing.requested += parseFloat(request.survey?.data?.matchingGiftAmount || "0");
      
      if (request.has_been_granted) {
        existing.granted += parseFloat(request.amount_granted || "0");
      }
      if (request.has_been_dispersed) {
        existing.disbursed += parseFloat(request.amount_granted || "0");
      }
      existing.count += 1;
      
      monthlyMap.set(monthYear, existing);
    });

  // Sort by date (newest first)
  return Array.from(monthlyMap.values()).sort((a, b) => {
    const [monthA, yearA] = a.month_year.split(' ');
    const [monthB, yearB] = b.month_year.split(' ');
    const dateA = new Date(`${monthA} 1, ${yearA}`);
    const dateB = new Date(`${monthB} 1, ${yearB}`);
    return dateB.getTime() - dateA.getTime();
  });
}
